import * as React from 'react';
import {
  useForwardTripState,
  useBackwardTripState,
} from 'pages/Settings/transport-routes/hooks';

export const useMapHelper = () => {
  const [forwardTripDirections, setForwardTripDirections]
    = React.useState<google.maps.DirectionsResult | null>(null);
  const [backwardTripDirections, setBackwardTripDirections]
    = React.useState<google.maps.DirectionsResult | null>(null);

  const {
    forwardTripCoordinates,
    forwardTripStopLocations,
  } = useForwardTripState();
  const {
    backwardTripCoordinates,
    backwardTripStopLocations,
  } = useBackwardTripState();

  const mapCenter = React.useMemo((): google.maps.LatLngLiteral | undefined => {
    if (
      !forwardTripCoordinates?.origin_lat ||
      !forwardTripCoordinates?.origin_lng ||
      !forwardTripCoordinates?.destination_lat ||
      !forwardTripCoordinates?.destination_lng
    ) {
      return undefined;
    }

    const origin = {
      lat: parseFloat(forwardTripCoordinates.origin_lat),
      lng: parseFloat(forwardTripCoordinates.origin_lng),
    };

    const destination = {
      lat: parseFloat(forwardTripCoordinates.destination_lat),
      lng: parseFloat(forwardTripCoordinates.destination_lng),
    };

    return {
      lat: (origin.lat + destination.lat) / 2,
      lng: (origin.lng + destination.lng) / 2,
    };
  }, [forwardTripCoordinates]);

  const forwardTripOrigin = React.useMemo((): google.maps.LatLngLiteral | undefined => {
    if (
      !forwardTripCoordinates?.origin_lat ||
      !forwardTripCoordinates?.origin_lng
    ) {
      return undefined;
    }

    return {
      lat: parseFloat(forwardTripCoordinates.origin_lat),
      lng: parseFloat(forwardTripCoordinates.origin_lng),
    };
  }, [forwardTripCoordinates]);

  const forwardTripDestination = React.useMemo((): google.maps.LatLngLiteral | undefined => {
    if (
      !forwardTripCoordinates?.destination_lat ||
      !forwardTripCoordinates?.destination_lng
    ) {
      return undefined;
    }

    return {
      lat: parseFloat(forwardTripCoordinates.destination_lat),
      lng: parseFloat(forwardTripCoordinates.destination_lng),
    };
  }, [forwardTripCoordinates]);

  const backwardTripOrigin = React.useMemo((): google.maps.LatLngLiteral | undefined => {
    if (
      !backwardTripCoordinates?.origin_lat ||
      !backwardTripCoordinates?.origin_lng
    ) {
      return undefined;
    }

    return {
      lat: parseFloat(backwardTripCoordinates.origin_lat),
      lng: parseFloat(backwardTripCoordinates.origin_lng),
    };
  }, [backwardTripCoordinates]);

  const backwardTripDestination = React.useMemo((): google.maps.LatLngLiteral | undefined => {
    if (
      !backwardTripCoordinates?.destination_lat ||
      !backwardTripCoordinates?.destination_lng
    ) {
      return undefined;
    }

    return {
      lat: parseFloat(backwardTripCoordinates.destination_lat),
      lng: parseFloat(backwardTripCoordinates.destination_lng),
    };
  }, [backwardTripCoordinates]);

  const googleMapsForwardDirectionsOptions = React.useMemo((): google.maps.DirectionsRequest | null => {
    if (!forwardTripOrigin || !forwardTripDestination) return null;

    return {
      origin: forwardTripOrigin,
      destination: forwardTripDestination,
      waypoints: forwardTripStopLocations.map(stop => ({
        stopover: false,
        location: {
          lat: parseFloat(stop.lat),
          lng: parseFloat(stop.lng),
        },
      })),
      travelMode: 'DRIVING',
    } as google.maps.DirectionsRequest;
  }, [
    forwardTripOrigin,
    forwardTripDestination,
    forwardTripStopLocations,
  ]);

  const googleMapsBackwardDirectionsOptions = React.useMemo((): google.maps.DirectionsRequest | null => {
    if (!backwardTripOrigin || !backwardTripDestination) return null;

    return {
      origin: backwardTripOrigin,
      destination: backwardTripDestination,
      waypoints: backwardTripStopLocations.map(stop => ({
        stopover: false,
        location: {
          lat: parseFloat(stop.lat),
          lng: parseFloat(stop.lng),
        },
      })),
      travelMode: 'DRIVING',
    } as google.maps.DirectionsRequest;
  }, [
    backwardTripOrigin,
    backwardTripDestination,
    backwardTripStopLocations,
  ]);

  const handleForwardDirectionsServiceResponse = React.useCallback((
    result: google.maps.DirectionsResult | null,
    status: google.maps.DirectionsStatus,
  ) => {
    if (status !== 'OK' || !result) return;

    setForwardTripDirections(result);
  }, []);

  const handleBackwardDirectionsServiceResponse = React.useCallback((
    result: google.maps.DirectionsResult | null,
    status: google.maps.DirectionsStatus,
  ) => {
    if (status !== 'OK' || !result) return;

    setBackwardTripDirections(result);
  }, []);

  return {
    mapCenter,
    forwardTripOrigin,
    forwardTripDestination,
    backwardTripOrigin,
    backwardTripDestination,
    googleMapsForwardDirectionsOptions,
    googleMapsBackwardDirectionsOptions,
    handleForwardDirectionsServiceResponse,
    handleBackwardDirectionsServiceResponse,
    forwardTripDirections,
    backwardTripDirections,
  };
};